import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const MaldivesPage = () => {
  const maldivesData = {
    name: 'Maldives',
    region: 'South Asia / Indian Ocean',
    color: '#5080B0',
    metaTitle: 'Pharma & Nutraceutical Registration in Maldives | MFDA | CTD/ACTD-Aligned Dossiers',
    metaDescription: 'Explore how to register pharmaceutical and nutraceutical products in the Maldives. MFDA requirements, registration process, CTD/ACTD alignment, supplement regulation, key documents and opportunities in this import-dependent island market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Maldives',
      subtitle: 'A small but affluent island market with almost full dependence on imported medicines, regulated by MFDA with CTD/ACTD guidance and high relevance for chronic & wellness products.',
      cta: 'Start Your Maldives Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~560K' },
      { label: 'Income Level', value: 'Upper-middle income' },
      { label: 'Health Expenditure (% of GDP)', value: '~8%' },
      { label: 'Pharma Import Dependence', value: '~100%' },
      { label: 'Local Manufacturing', value: 'Negligible' },
      { label: 'Regulatory Authority', value: 'MFDA' }
    ],
    
    marketOverview: {
      title: 'Market Overview: High Spend, Import-Only, Small Volume',
      stats: [
        'No local pharmaceutical manufacturing of significance',
        'Imports nearly all medicines (~100% dependence)',
        'MFDA created under Ministry of Health - handles medicines, food, medical devices',
        'Medicine Regulation No: R-46/2014 (mandatory product registration)',
        'Tourism-affluent context supports wellness and immunity supplements',
        'High disposable income segments present',
        'High per-capita medicine spend despite small population',
        'Streamlined import route for quality products'
      ],
      opportunities: [
        'High-value, smaller volume entries',
        'Fewer local competitors',
        'Premium generics positioning opportunity',
        'High-quality nutraceuticals market',
        'Tourism-linked wellness demand',
        'Expatriate and affluent domestic segments'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: Travel-Health + NCD Pressures',
      subtitle: 'Rising NCD burden with tourism and lifestyle exposure driving demand for chronic disease management and wellness products.',
      categories: [
        {
          name: 'Non-Communicable Diseases — Major Burden',
          items: [
            'NCDs account for large share of mortality',
            'Essential medicines list grows year by year',
            'Overweight/obesity increasingly relevant',
            'Diabetes rising due to tourism and lifestyle exposure',
            'Pharma: Antidiabetics, antihypertensives, lipid-lowering drugs, lifestyle disease treatment',
            'Nutra: Metabolic health, joint wellness, vitamins/minerals, immunity for expatriate & tourism populations'
          ]
        },
        {
          name: 'Infectious Diseases & Travel-Health',
          items: [
            'Travel-associated health imports and challenges',
            'Tourism health risks management',
            'Import-only structure requires quality and stability assurance',
            'Supply chain reliability critical for regulatory acceptance',
            'Pharma: High-quality generics for NCDs, essential medicines, hospital/tertiary care supplies for island clinics',
            'Nutra: High-end wellness formulas, lifestyle supplements, vitamins & minerals for tourists/expats and affluent segments'
          ]
        },
        {
          name: 'Best-Fit Product Categories',
          items: [
            'Pharma: High-quality generics for NCDs (antidiabetics, antihypertensives), hospital supplies, lifestyle-disease products',
            'Nutra: Premium wellness (metabolic support, joint health, vitamins/minerals, immunity) for affluent consumers and tourism demand'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Maldives Food and Drug Authority (MFDA)',
      description: 'MFDA regulates medicines, medical devices, food and supplements under Ministry of Health. Medicine Regulation No: R-46/2014 (effective 20 Jan 2015) with amendment R-49/2016. Guideline on Product Registration and Approval (Issue 02, Feb 2020, revised Jan 2021) details requirements.',
      pharma: {
        timeline: 'Risk-based review with reliance on Reference Regulatory Authorities may expedite evaluation',
        requirements: [
          'Local importer/MAH licensed by MFDA must submit applications',
          'All imported medicines must be registered',
          'Dossier per MFDA guideline - administrative forms, GMP compliance, COA, stability',
          'Manufacturing site information required',
          'MFDA accepts CTD/ACTD-aligned dossiers',
          'Reliance on SRA decisions for expedited review',
          'Risk-based technical evaluation by MFDA staff',
          'Quality, safety, efficacy assessment with query process',
          'Decision via National Pharmaceutical Board',
          'Market authorization and import licenses for each shipment',
          'Renewals and compliance monitoring apply'
        ],
        insight: 'MFDA uses reliance-based approach on Reference Regulatory Authorities. Companies with SRA approvals may achieve faster access. Though guideline doesn\'t explicitly mandate ICH-CTD, CTD/ACTD-aligned dossiers are accepted.'
      },
      nutra: {
        timeline: 'Depends on classification and importer coordination',
        requirements: [
          'MFDA regulates both medicines and food - less detailed public nutra guidance',
          'Full compliance, correct classification (food vs medicine) essential',
          'Labelling in regulatory accepted languages required',
          'Importer registration and manufacturer GMP mandatory',
          'No local manufacturers - all supplements imported',
          'Must meet import-control standards',
          'Full composition and finished product COA required',
          'Label artwork per MFDA/food regulations (storage, expiry, no therapeutic claims)',
          'If claims suggest disease treatment, may be classified as medicine'
        ],
        insight: 'Positioning critical - claims must not stray into disease treatment. While detailed public guidelines limited, imported supplements must meet MFDA import/licensing requirements with clear food vs medicine classification.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with MFDA',
      steps: [
        'Local Importer / MAH - Must be licensed by MFDA to submit applications',
        'Registration requirement - All imported medicines must be registered',
        'Dossier Preparation - Per MFDA guideline with administrative forms',
        'GMP compliance, COA, stability, manufacturing site info inclusion',
        'Evidence of RRA registration may help under reliance approach',
        'Technical Evaluation - MFDA staff assess quality, safety, efficacy',
        'Query process and response management',
        'National Pharmaceutical Board decision',
        'Market Authorization issuance',
        'Import licenses obtained for each shipment',
        'Post-Approval Variations - Follow Variation Guidelines for changes'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical & Wellness Products in Maldives',
      steps: [
        'Classification determination - Food vs medicine based on claims',
        'Local importer/MAH application for registration/clearance',
        'Full composition and finished product COA preparation',
        'Manufacturer GMP certification requirement',
        'Label artwork compliance - MFDA/food regulations',
        'Storage, expiry information, no therapeutic claims (unless approved)',
        'Import control standards compliance',
        'Coordination with MFDA for clearance and permit',
        'Positioning verification - avoid disease treatment claims',
        'Registration/clearance approval',
        'Import licensing for each shipment'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals': [
        'Application form per MFDA guideline',
        'Dossier per guideline - administrative, technical sections',
        'GMP certificate for manufacturing site',
        'COA and stability data',
        'Manufacturing-site information',
        'Evidence of RRA registration (may expedite under reliance)',
        'Label artwork and packaging files',
        'Import licence arrangements',
        'Fee payment',
        'Local authorized agent details'
      ],
      'Nutraceuticals / Supplements': [
        'Application via local importer/MAH',
        'Full formula and finished-product COA',
        'Manufacturer GMP/quality certificate',
        'Label/packaging artwork',
        'Storage/expiry details',
        'Evidence of regulatory status in country of origin',
        'Classification documentation (food vs medicine)',
        'Import control compliance documents'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Maldives Should Be Part of Your Expansion Strategy',
      reasons: [
        'High per-capita health spend despite small population',
        'Essentially import-only pharmaceutical market (100% dependence)',
        'Premium product entry opportunity',
        'Strong tourism sector raises wellness & nutraceutical demand',
        'Affluent expatriate population',
        'MFDA reliance-based approach - faster access for SRA-approved products',
        'Strategic "premium-niche" in South Asia/Indian Ocean cluster',
        'Fewer local competitors in import market'
      ],
      bestPortfolio: [
        'Pharma: High-quality generics for NCDs (antidiabetics, antihypertensives), hospital supplies, lifestyle-disease products',
        'Nutra: Premium wellness (metabolic support, joint health, vitamins/minerals, immunity) for affluent consumers and tourism demand'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Register & Launch in Maldives',
      services: [
        'Portfolio & Market Fit Analysis - Evaluate products vs Maldives demand and import dynamics',
        'Dossier Development & Import-Strategy - Craft/adapt CTD/ACTD assets to MFDA guidelines',
        'Local MAH/Importer Coordination - Support partner selection and documentation',
        'Supplement Classification & Compliance - Ensure correct positioning of wellness products',
        'Label & Claims Review - Adapt artwork for MFDA requirements and tourism/expat consumers',
        'Lifecycle & Support - Assist with renewals, variations, import protocols, MFDA queries'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines and supplements in Maldives?',
        answer: 'The Maldives Food and Drug Authority (MFDA) regulates medicines, medical devices, food and supplements under the Ministry of Health.'
      },
      {
        question: 'What dossier format is required?',
        answer: 'MFDA\'s guideline uses Medicine Regulation R-46/2014 and the "Guideline on Product Registration and Approval of Medicines" (2020). The authority uses reliance and may accept CTD/ACTD-aligned dossiers.'
      },
      {
        question: 'Is a local applicant required?',
        answer: 'Yes. An importer or locally licensed entity must apply for registration and import licences.'
      },
      {
        question: 'Are supplements regulated?',
        answer: 'Yes. While detailed public guidelines are limited, supplements imported to Maldives must meet MFDA\'s import/licensing requirements and classification (food vs medicine) must be clear.'
      },
      {
        question: 'Is Maldives a good market for Indian exporters?',
        answer: 'Yes, particularly for higher-value products, niche wellness supplements or quality generics, due to import-dependence, reliance orientation and tourism-linked affluent demand.'
      }
    ]
  };

  return <CountryTemplate countryData={maldivesData} />;
};
